
import { methods } from 'http-constants';
import { getUserByToken, setUserToken, removeUserToken } from '../crud/auth.crud';
import pluralize from 'pluralize';
import axios from 'axios';
import P from 'bluebird';
import _ from 'lodash';
import page from '../constants/pages/page';
import errorCodes from '../constants/pages/errorCode';

export const baseUrl = process.env.REACT_APP_SERVICE_URI;
const defaultOptions = {
    crossDomain: true,
    normalize: true,
    json: true
};

const defaultOptionsFind = {
    pageSize: page.pageSize
};
const defaultOptionsGetById = {};
const defaultOptionsGetOne = {
    pageSize: 1,
    page: 1
};

const mapKeys = {
    baseUrl: 'baseURL',
    uri: 'url',
    qs: 'params',
    body: 'data'
};

class Service {
    constructor (name, basePath, options = {}) {
        this.name = pluralize.singular(name);
        this.serviceName = name;
        this.basePath = basePath || `/${name}`;
        this.r = axios.create(options);
        this.r.interceptors.response.use(response => response, e => {
            const origRequest = e.config;

            if (e.response?.data?.extra?.path === '/tokens/refresh') {
                removeUserToken();
                window.location.replace(`${window.location.origin}/logout`);
                return false;
            }

            if (e.response && e.response.data && e.response.data.code === errorCodes.unauthorized && !origRequest._retry) {
                origRequest._retry = true;

                // Get logged in user
                const user = getUserByToken();

                if (!user || !user.data) {
                    // Check if this is a login request — if so, let error propagate
                    const requestUrl = origRequest.url || '';
                    if (requestUrl.includes('/auth/login')) {
                        return Promise.reject(e);
                    }
                    removeUserToken();
                    window.location.replace(`${window.location.origin}/logout`);
                    return false;
                }

                const token = user.data.token.access_token;
                const refreshToken = user.data.token.refresh_token;

                return this.refreshAccessToken(token, refreshToken)
                    .then(newToken => {
                        setUserToken(newToken.data);
                        const newAccessToken = newToken.data.access_token;
                        this.r.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
                        origRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                        return this.r.request(origRequest);
                    });
            }

            return Promise.reject(e);
        });
    }

    get defaultOptionsFind () {
        return defaultOptionsFind;
    }

    get defaultOptionsGetById () {
        return defaultOptionsGetById;
    }

    get defaultOptionsGetOne () {
        return defaultOptionsGetOne;
    }

    find (options = {}) {
        const data = options.data || options.qs || options.params || (!options.url ? options : {});
        const opts = {
            url: options.url || `${this.basePath}${this.basePath}`,
            params: _.defaultsDeep({}, data, this.defaultOptionsFind)
        };

        return this.get(opts).then(models => {
            if (options && options.normalize === false) {
                return models;
            }
            return this.normalizeAll(models);
        });
    }

    getById (id, options = {}) {
        const data = options.data || options.qs || options.params || (!options.url ? options : {});
        const opts = {
            url: options.url || `${this.basePath}${this.basePath}/${id}`,
            params: _.defaultsDeep({}, data, this.defaultOptionsGetById)
        };

        return this.get(opts).then(model => {
            if (options && options.normalize === false) {
                return model;
            }
            return this.normalize(model);
        });
    }

    getOne (options = {}) {
        const data = options.data || options.qs || options.params || (!options.url ? options : {});
        const opts = {
            url: options.url || `${this.basePath}${this.basePath}`,
            params: _.defaultsDeep({}, data, this.defaultOptionsGetOne)
        };

        return this.get(opts).then(models => {
            const model = (models && models.length && models[0]) || null;

            if (options && options.normalize === false) {
                return model;
            }
            return this.normalize(model);
        });
    }

    request (method, options) {
        return P.bind(this)
            .then(() => {
                return new P((resolve, reject) => {
                    // Get logged in user
                    const user = getUserByToken();
                    if (user && user.data) {
                        const token = user.data.token.access_token;
                        this.r.defaults.headers.common.Authorization = `Bearer ${token}`;
                    }

                    const opts = this.prepareOptions(method, options);

                    if (!opts.json) {
                        this.r.defaults.headers['Content-Type'] = 'multipart/form-data';

                        if (opts.data.file) {
                            const file = new FormData();

                            file.append('filename', opts.data.file.name);
                            file.append('file', opts.data.file.blobFile);
                            opts.data = file;
                        }
                    }

                    return this.r
                        .request(opts)
                        .then(res => resolve(opts.raw ? res : res.data))
                        .catch(error => reject(error));
                });
            });
    }

    refreshAccessToken (accessToken, refreshToken) {
        const opts = {
            url: `${baseUrl}/auth/tokens/refresh`,
            method: 'POST',
            data: {
                accessToken,
                refreshToken
            }
        };

        return this.r
            .request(opts);
    }

    prepareOptions (method, options) {
        if (_.isString(options)) {
            options = {
                url: options,
            };
        }

        if (!_.startsWith(options.url, 'http')) {
            options.url = `${baseUrl}${options.url}`;
        }

        options.method = method || 'get';

        options = _.defaultsDeep({}, _.mapKeys(options, (value, key) => mapKeys[key] || key), defaultOptions);

        return options;
    }

    normalizeAll (models = []) {
        if (_.isNil(models)) {
            return models;
        }

        if (!_.isArray(models)) {
            models = [models];
        }

        return _.map(models, model => this.normalize(model));
    }

    normalize (model) {
        if (_.isNil(model)) {
            return model;
        }

        // Prepare attributes
        if (model.attributes) {
            model.rawAttributes = model.attributes;
            model.attributes = _.keyBy(model.attributes, 'description');
        }

        return model;
    }
}

_.each(_.map(methods, method => method.toLowerCase()), method => {
    Service.prototype[method] = function (options) {
        return this.request(method, options);
    };
});

export default Service;
